import React from "react";
import { Calendar, ChevronDown, ArrowRight } from "lucide-react";
import "./VoucherRedemptionTab.css";

// ================== DATA (استبدلها بالداتا من الـ API) ==================
const summaryStats = [
  { label: "Total Redemption", value: "1,284" },
  { label: "Unique Users", value: "982" },
  { label: "Average Order Value", value: "131 EGP" },
  { label: "Total Redeemed Value", value: "168,000 EGP" },
];

const redemptionRate = {
  label: "Redemption Rate",
  value: "33.6%",
  suffix: "vs Initial Value",
};

const chartLabels = [
  "Mar 10",
  "Mar 17",
  "Mar 24",
  "Mar 31",
  "Apr 07",
  "Apr 21",
  "Apr 28",
  "May 05",
  "May 12",
  "May 19",
  "May 26",
  "May 31",
];
const chartValues = [20, 65, 110, 140, 165, 140, 120, 110, 150, 185, 170, 205];
const chartDots = [0, 1, 2, 4, 7, 9, 10, 11];

const statusData = [
  { label: "Completed", count: 1152, pct: "89.6%", color: "#14B8A6" },
  { label: "Partially Used", count: 64, pct: "5.0%", color: "#F5A623" },
  { label: "Expired", count: 48, pct: "3.7%", color: "#4B5563" },
  { label: "Cancelled", count: 20, pct: "1.6%", color: "#DC2626" },
];
const statusTotal = 256;

const channelData = [
  { label: "Mobile App", count: 912, pct: "71.0%" },
  { label: "Website", count: 232, pct: "18.1%" },
  { label: "Call Center", count: 96, pct: "7.5%" },
  { label: "POS", count: 44, pct: "3.4%" },
];

const topUsers = [
  { name: "Osama Ali", count: 24 },
  { name: "Ahmed Hassan", count: 18 },
  { name: "Amr Hassan", count: 17 },
  { name: "Mona Salah", count: 15 },
];

const insights = [
  "Redemption rate is 12% higher than average for similar vouchers.",
  "Mobile App is the top channel with 71% of total redemptions.",
  "Most redemptions occur on weekends.",
];

const tableRows = Array.from({ length: 7 }).map(() => ({
  id: "RED-1248",
  user: "Mona Salah",
  date: "May 18, 2025 - 02:15 pm",
  orderId: "ORD-3549",
  orderValue: "420 EGP",
  discountValue: "80 EGP",
  status: "Completed",
  channel: "Mobile App",
}));

const avatarColors = ["#FDE68A", "#BFDBFE", "#FBCFE8", "#C7D2FE"];

// ================== SUB COMPONENTS ==================
function StepBadge({ n }) {
  return <span className="vr-step-badge">{n}</span>;
}

function LineChart({ labels, values, dots }) {
  const width = 1000;
  const height = 260;
  const padLeft = 40;
  const padBottom = 10;
  const maxY = 250;
  const gridValues = [0, 50, 100, 150, 200, 250];

  const plotW = width - padLeft;
  const plotH = height - padBottom;
  const stepX = plotW / (labels.length - 1);

  const points = values.map((v, i) => ({
    x: padLeft + i * stepX,
    y: plotH - (v / maxY) * plotH,
  }));

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${plotH} L ${points[0].x} ${plotH} Z`;

  return (
    <div className="vr-chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height + 20}`}
        className="vr-chart-svg"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="vrAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridValues.map((g) => {
          const y = plotH - (g / maxY) * plotH;
          return (
            <g key={g}>
              <line
                x1={padLeft}
                x2={width}
                y1={y}
                y2={y}
                className="vr-grid-line"
              />
              <text
                x={padLeft - 10}
                y={y + 4}
                className="vr-axis-label"
                textAnchor="end"
              >
                {g}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill="url(#vrAreaFill)" />
        <path d={linePath} className="vr-line-path" />

        {points.map(
          (p, i) =>
            dots.includes(i) && (
              <circle key={i} cx={p.x} cy={p.y} r="5" className="vr-line-dot" />
            ),
        )}

        {labels.map((l, i) => (
          <text
            key={l}
            x={padLeft + i * stepX}
            y={plotH + 24}
            className="vr-axis-label"
            textAnchor="middle"
          >
            {l}
          </text>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({ data, total }) {
  const size = 140;
  const cx = size / 2;
  const cy = size / 2;
  const r = 55;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * r;

  let cumulative = 0;
  const segments = data.map((d) => {
    const pct = parseFloat(d.pct) / 100;
    const length = pct * circumference;
    const offset = cumulative;
    cumulative += length;
    return { ...d, length, offset };
  });

  return (
    <div className="vr-donut-chart">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {segments.map((s, i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${s.length} ${circumference - s.length}`}
              strokeDashoffset={-s.offset}
            />
          ))}
        </g>
      </svg>
      <div className="vr-donut-center">
        <span className="vr-donut-total">{total}</span>
        <span className="vr-donut-total-label">Total</span>
      </div>
    </div>
  );
}

// ================== MAIN COMPONENT ==================
export default function VoucherRedemptionTab() {
  return (
    <div className="vr-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="vr-main">
        <div className="vr-panel">
          {/* 1. Redemption Summary */}
          <div className="vr-panel-section">
            <div className="vr-card-header">
              <StepBadge n={1} />
              <h3 className="vr-card-title">Redemption Summary</h3>
            </div>

            <div className="vr-summary-grid">
              {summaryStats.map((s) => (
                <div className="vr-summary-item" key={s.label}>
                  <span className="vr-summary-label">{s.label}</span>
                  <span className="vr-summary-value">{s.value}</span>
                </div>
              ))}
            </div>

            <div className="vr-summary-inner-divider" />

            <div className="vr-summary-item">
              <span className="vr-summary-label">{redemptionRate.label}</span>
              <span className="vr-summary-value">
                {redemptionRate.value}{" "}
                <span className="vr-summary-suffix">
                  {redemptionRate.suffix}
                </span>
              </span>
            </div>
          </div>
          <div className="vr-panel-divider" />
          {/* 2. Redemption Over Time */}
          <div className="vr-panel-section">
            <div className="vr-card-header-row">
              <div className="vr-card-header">
                <StepBadge n={2} />
                <h3 className="vr-card-title">Redemption Over Time</h3>
              </div>

              <div className="vr-filters">
                <button className="vr-select-btn">
                  Daily <ChevronDown size={14} />
                </button>
                <button className="vr-date-btn">
                  <Calendar size={14} />
                  Mar 10, 2025 - May 31, 2025
                </button>
              </div>
            </div>
            <LineChart
              labels={chartLabels}
              values={chartValues}
              dots={chartDots}
            />
          </div>
          <div className="vr-panel-divider" />
          {/* 3. Recent Redemptions */}
          <div className="vr-panel-section">
            <div className="vr-card-header-row">
              <div className="vr-card-header">
                <StepBadge n={3} />
                <h3 className="vr-card-title">Recent Redemptions</h3>
              </div>
              <a className="vr-link" href="#">
                View all <ArrowRight size={14} />
              </a>
            </div>

            <div className="vr-table-wrap">
              <table className="vr-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Date & Time</th>
                    <th>Order ID</th>
                    <th>Order Value</th>
                    <th>Discount Value</th>
                    <th>Status</th>
                    <th>Channel</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, i) => (
                    <tr key={i}>
                      <td>{row.id}</td>
                      <td>{row.user}</td>
                      <td>{row.date}</td>
                      <td>{row.orderId}</td>
                      <td>{row.orderValue}</td>
                      <td>{row.discountValue}</td>
                      <td>
                        <span
                          className={`vr-status-pill vr-status-${row.status.toLowerCase().replace(/\s/g, "-")}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>{row.channel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* ---------- SIDEBAR ---------- */}
      <div className="vr-sidebar">
        {/* Redemption by Status */}
        <div className="vr-card">
          <h3 className="vr-card-title">Redemption by Status</h3>
          <div className="vr-status-row">
            <DonutChart data={statusData} total={statusTotal} />
            <div className="vr-status-legend">
              {statusData.map((s) => (
                <div className="vr-legend-item" key={s.label}>
                  <span
                    className="vr-legend-dot"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="vr-legend-label">
                    {s.label} ({s.count})
                  </span>
                  <span className="vr-legend-pct">{s.pct}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Redemption by Channel */}
        <div className="vr-card">
          <h3 className="vr-card-title">Redemption by Channel</h3>
          <div className="vr-channel-list">
            {channelData.map((c, i) => (
              <div className="vr-channel-item" key={c.label}>
                <span className="vr-channel-num">{i + 1}</span>
                <span className="vr-channel-label">{c.label}</span>
                <span className="vr-channel-count">{c.count}</span>
                <span className="vr-channel-pct">{c.pct}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Top Redeeming Users */}
        <div className="vr-card">
          <div className="vr-card-header-row">
            <h3 className="vr-card-title">Top Redeeming Users</h3>
            <a className="vr-link" href="#">
              View Full Report <ArrowRight size={14} />
            </a>
          </div>
          <div className="vr-users-list">
            {topUsers.map((u, i) => (
              <div className="vr-user-item" key={u.name}>
                <span
                  className="vr-user-avatar"
                  style={{
                    backgroundColor: avatarColors[i % avatarColors.length],
                  }}
                >
                  {u.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </span>
                <span className="vr-user-name">{u.name}</span>
                <span className="vr-user-count">{u.count}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Insights */}
        <div className="vr-card">
          <div className="vr-card-header-row">
            <h3 className="vr-card-title">Insights</h3>
            <a className="vr-link" href="#">
              View More Insights <ArrowRight size={14} />
            </a>
          </div>
          <ul className="vr-insights-list">
            {insights.map((text, i) => (
              <li key={i} className="vr-insight-item">
                <span className="vr-insight-dot" />
                <span className="vr-insight-text">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
